import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { DateTime } from 'luxon';

import { Reservation, ReservationGuest } from 'src/models/schedule-weekly-view.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';
import { Util } from 'src/shared/util/util';
import { ReservationLockCodesService } from 'src/shared/services/reservation-lock-codes.service';

@Component({
  selector: 'app-reservation-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reservation-popover.component.html',
  styleUrls: ['./reservation-popover.component.scss'],
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, TranslationPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReservationPopoverComponent implements OnInit {
  private readonly util = inject(Util);
  private readonly reservationLockCodes = inject(ReservationLockCodesService);

  @Input() reservation!: Reservation;
  @Input() address?: string;
  @Input() addressId?: number | string;

  @Output() readonly guidebookClick = new EventEmitter<Reservation>();
  @Output() readonly detailsClick = new EventEmitter<Reservation>();
  @Output() readonly messageClick = new EventEmitter<Reservation>();
  @Output() readonly dismiss = new EventEmitter<void>();
  @Output() readonly hoverChange = new EventEmitter<boolean>();

  readonly lockCodes = signal<{ label: string; code: string; deviceId: number }[]>([]);
  readonly isLoadingLockCodes = signal(false);

  ngOnInit(): void {
    void this.loadLockCodes();
  }

  // The parents render this popover lazily (matMenuContent), so this runs only
  // when a popover actually opens. The skeleton shows only for properties that
  // have a lock, so the popover doesn't jump for the many that don't.
  private async loadLockCodes(): Promise<void> {
    const raw = (this.reservation?.object ?? this.reservation) as any;
    const reservationId = Number(raw?.id);
    const addressId = Number(this.addressId || raw?.address_id);
    // An inquiry's id is not a guest reservation id, so it can't be looked up.
    if (this.isInquiry || !reservationId || !addressId) {
      return;
    }
    try {
      const locks = await this.reservationLockCodes.getAssignedLocks(addressId);
      if (!locks.length) {
        return;
      }
      this.isLoadingLockCodes.set(true);
      const codes = await this.reservationLockCodes.getLockCodes(reservationId, addressId);
      this.lockCodes.set(codes.map((item) => ({
        label: codes.length > 1 ? item.deviceName : 'Lock Code',
        code: item.code,
        deviceId: item.deviceId,
      })));
    } catch (_err) {
      // No Seam integration or the lock list failed — the row stays hidden.
    } finally {
      this.isLoadingLockCodes.set(false);
    }
  }

  copyLockCode(code: string): void {
    this.util.copyToClipBoard(code);
  }

  onMouseEnter(): void {
    this.hoverChange.emit(true);
  }

  onMouseLeave(): void {
    this.hoverChange.emit(false);
  }

  logoFailed = false;
  profilePictureFailed = false;

  get sourceLogoUrl(): string | undefined {
    if (this.logoFailed) {
      return undefined;
    }
    return this.reservation?.sourceLogoUrl;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }

  get guestProfilePicture(): string | undefined {
    if (this.profilePictureFailed) {
      return undefined;
    }
    return this.reservation?.guestProfilePicture || undefined;
  }

  onProfilePictureError(): void {
    this.profilePictureFailed = true;
  }

  get guestFullName(): string {
    const fromParts = [this.reservation?.guestFirstName, this.reservation?.guestLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fromParts || this.reservation?.guestName?.trim() || '';
  }

  get headerTitle(): string {
    return this.reservation?.title || 'Reservation';
  }

  get initials(): string {
    const base = this.guestFullName || this.headerTitle || '?';
    const parts = base.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  get avatarGradient(): string {
    return this.util.getAvatarGradient(this.initials || 'A');
  }

  get canMessage(): boolean {
    const guest = this.primaryGuest;
    return Boolean(this.reservation?.phone || guest?.phone || guest?.chatRoomKey);
  }

  get hasGuestInfo(): boolean {
    return Boolean(this.guestRows.length || this.guestFullName || this.notes);
  }

  get guestRows(): { id: number; name: string; isBooker: boolean; email?: string; phone?: string }[] {
    return (this.reservation?.guests ?? []).map((guest: ReservationGuest) => ({
      id: guest.id,
      name: guest.name?.trim() || 'Guest',
      isBooker: Boolean(guest.isBooker),
      email: guest.email || undefined,
      phone: this.formatPhone(guest.phone),
    }));
  }

  private get primaryGuest(): ReservationGuest | undefined {
    const guests = this.reservation?.guests ?? [];
    return guests.find((guest) => guest.isBooker)
      ?? guests.find((guest) => guest.isStaying)
      ?? guests[0];
  }

  get notes(): string | undefined {
    return this.reservation?.notes || undefined;
  }

  get formattedPhone(): string | undefined {
    return this.formatPhone(this.reservation?.phone);
  }

  private formatPhone(raw?: string | null): string | undefined {
    if (!raw) {return undefined;}
    const digits = raw.replace(/\D+/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      const rest = digits.slice(1);
      return `(${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
  }

  get platformLabel(): string | undefined {
    const platform = this.reservation?.platform;
    if (!platform) {return undefined;}
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  get bookedOnLabel(): string | undefined {
    if (!this.platformLabel || !this.reservation?.bookingDate) {return undefined;}
    const isManual = (this.reservation.platform || '').toLowerCase() === 'manual';
    const prefix = isManual ? 'Booked manually' : `Booked on ${this.platformLabel}`;
    const dt = DateTime.fromISO(this.reservation.bookingDate);
    if (!dt.isValid) {
      return prefix;
    }
    return `${prefix} on ${dt.toFormat('MMM d')} at ${dt.toFormat('h:mma').toLowerCase()}`;
  }

  get isInquiry(): boolean {
    return this.reservation?.state === 'inquiry';
  }

  // Inquiry header is guest-forward: the asking guest is the title, and the
  // channel + stay meta collapse into one subtitle line.
  get inquiryTitle(): string {
    return this.guestFullName || this.headerTitle;
  }

  get inquirySubtitle(): string {
    const source = this.reservation?.sourceName;
    const parts = [source ? `${source} inquiry` : 'Inquiry'];
    if (this.reservation?.title) {
      parts.push(this.reservation.title);
    }
    const nights = this.reservation?.nights;
    if (nights) {
      parts.push(`${nights} ${nights === 1 ? 'night' : 'nights'}`);
    }
    return parts.join(' · ');
  }

  get inquiryFirstName(): string {
    return this.guestFullName.split(/\s+/)[0] || 'The guest';
  }

  get inquiryBannerTitle(): string {
    return this.needsResponse
      ? `${this.inquiryFirstName} is waiting for a reply`
      : 'Inquiry';
  }

  get inquiryBannerHint(): string | null {
    if (this.needsResponse) {
      return 'Pre-approve or decline — quick replies protect your response rate';
    }
    const count = this.reservation?.followUpCount;
    return count ? `${count} follow-up${count === 1 ? '' : 's'}` : null;
  }

  // An inquiry's amount is the would-be host payout, not a charged total —
  // hide it entirely when the channel didn't capture a price (0 / undefined).
  get inquiryPayout(): string | null {
    return this.reservation?.total ? this.formatMoney(this.reservation.total) : null;
  }

  handleDetailsClick(): void {
    this.detailsClick.emit(this.reservation);
  }

  handleGuidebookClick(): void {
    this.guidebookClick.emit(this.reservation);
  }

  handleMessageClick(): void {
    this.messageClick.emit(this.reservation);
  }

  get statusLabel(): string {
    switch (this.reservation?.state) {
      case 'inquiry':
        return this.needsResponse ? 'Inquiry — needs response' : 'Inquiry';
      case 'quote':
        return 'Quote sent';
      case 'hold':
        return 'Hold';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No show';
      default:
        break;
    }
    const today = DateTime.now().startOf('day');
    const realStartDate = this.reservation?.period?.startDate ?? this.reservation?.startDate;
    const realEndDate = this.reservation?.period?.endDate ?? this.reservation?.endDate;
    const start = realStartDate
      ? DateTime.fromISO(realStartDate).startOf('day')
      : null;
    const end = realEndDate
      ? DateTime.fromISO(realEndDate).startOf('day')
      : null;
    if (start?.hasSame(today, 'day')) {return 'Checking in today';}
    if (end?.hasSame(today, 'day')) {return 'Checking out today';}
    if (start && end && start < today && end > today) {return 'Currently staying';}
    if (end && end < today) {return 'Past stay';}
    if (start) {
      const days = Math.round(start.diff(today, 'days').days);
      if (days === 1) {return 'Arrives tomorrow';}
      if (days > 0) {return `Arrives in ${days} days`;}
    }
    return 'Upcoming';
  }

  get needsResponse(): boolean {
    return this.reservation?.state === 'inquiry' && !this.reservation?.firstResponseAt;
  }

  get quoteExpiresInHours(): number | null {
    if (!this.reservation?.quoteValidUntil) {return null;}
    const until = DateTime.fromISO(this.reservation.quoteValidUntil);
    if (!until.isValid) {return null;}
    return Math.round(until.diff(DateTime.now(), 'hours').hours);
  }

  get quoteExpiresSoon(): boolean {
    const h = this.quoteExpiresInHours;
    return h !== null && h <= 24 && h > 0;
  }

  get quoteExpired(): boolean {
    const h = this.quoteExpiresInHours;
    return h !== null && h <= 0;
  }

  get quoteAgeLabel(): string | null {
    if (!this.reservation?.quoteSentAt) {return null;}
    const sent = DateTime.fromISO(this.reservation.quoteSentAt);
    if (!sent.isValid) {return null;}
    const hours = Math.max(0, Math.round(DateTime.now().diff(sent, 'hours').hours));
    if (hours < 1) {return 'just now';}
    if (hours < 24) {return `${hours}h ago`;}
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  }

  get holdExpiresInHours(): number | null {
    if (!this.reservation?.holdExpiresAt) {return null;}
    const until = DateTime.fromISO(this.reservation.holdExpiresAt);
    if (!until.isValid) {return null;}
    return Math.round(until.diff(DateTime.now(), 'hours').hours);
  }

  get holdExpiresSoon(): boolean {
    const h = this.holdExpiresInHours;
    return h !== null && h <= 24 && h > 0;
  }

  // A hold without a type is a Vrbo hold: dates blocked for a guest who
  // hasn't confirmed or paid yet.
  get holdBannerTitle(): string {
    const holdType = this.reservation?.holdType;
    return holdType ? `${holdType} hold` : 'Hold: awaiting guest confirmation';
  }

  get holdTimeRemainingLabel(): string | null {
    const h = this.holdExpiresInHours;
    if (h === null) {return null;}
    if (h <= 0) {return 'expired';}
    if (h < 1) {return '<1h left';}
    if (h < 24) {return `${h}h left`;}
    return `${Math.round(h / 24)}d left`;
  }

  get depositStatusLabel(): string | null {
    switch (this.reservation?.depositStatus) {
      case 'received':
        return 'Deposit received';
      case 'pending':
        return 'Deposit pending';
      case 'refunded':
        return 'Deposit refunded';
      case 'waived':
        return 'Deposit waived';
      default:
        return null;
    }
  }

  get reviewStatusLabel(): string | null {
    switch (this.reservation?.reviewStatus) {
      case 'pending':
        return 'Review pending';
      case 'left':
        return 'Review left';
      case 'received':
        return 'Review received';
      default:
        return null;
    }
  }

  get formattedQuoteAmount(): string | null {
    return this.formatMoney(this.reservation?.quoteAmount);
  }

  get formattedDepositAmount(): string | null {
    return this.formatMoney(this.reservation?.depositAmount);
  }

  get pricingRows(): { label: string; value: string }[] {
    const p = this.reservation?.pricingBreakdown;
    if (!p) {return [];}
    const rows: { label: string; value: string }[] = [];
    if (p.nightlyRate && p.nights) {
      rows.push({
        label: `${this.formatMoney(p.nightlyRate)} x ${p.nights} ${p.nights === 1 ? 'night' : 'nights'}`,
        value: this.formatMoney(p.nightlyRate * p.nights) ?? '',
      });
    }
    if (p.cleaningFee) {
      rows.push({ label: 'Cleaning fee', value: this.formatMoney(p.cleaningFee) ?? '' });
    }
    if (p.serviceFee) {
      rows.push({ label: 'Service fee', value: this.formatMoney(p.serviceFee) ?? '' });
    }
    if (p.taxes) {
      rows.push({ label: 'Taxes', value: this.formatMoney(p.taxes) ?? '' });
    }
    if (p.discounts) {
      rows.push({ label: 'Discount', value: `-${this.formatMoney(p.discounts)}` });
    }
    return rows;
  }

  get metaLine(): string {
    const parts: string[] = [];
    if (this.reservation?.nights) {
      parts.push(`${this.reservation.nights} ${this.reservation.nights === 1 ? 'night' : 'nights'}`);
    }
    if (this.reservation?.title) {
      parts.push(this.reservation.title);
    }
    return parts.join(' • ');
  }

  get guestCounts(): { icon: string; count: number; label: string }[] {
    const r = this.reservation;
    const out: { icon: string; count: number; label: string }[] = [];
    if (r?.totalAdults) {
      out.push({ icon: 'assets/svg/person.svg', count: r.totalAdults, label: r.totalAdults === 1 ? 'adult' : 'adults' });
    }
    if (r?.totalChildren) {
      out.push({ icon: 'assets/svg/people.svg', count: r.totalChildren, label: r.totalChildren === 1 ? 'child' : 'children' });
    }
    if (r?.totalInfants) {
      out.push({ icon: 'assets/svg/heart-outline.svg', count: r.totalInfants, label: r.totalInfants === 1 ? 'infant' : 'infants' });
    }
    if (r?.totalPets) {
      out.push({ icon: 'assets/svg/paw.svg', count: r.totalPets, label: r.totalPets === 1 ? 'pet' : 'pets' });
    }
    if (!out.length && r?.totalGuests) {
      out.push({ icon: 'assets/svg/person.svg', count: r.totalGuests, label: r.totalGuests === 1 ? 'guest' : 'guests' });
    }
    return out;
  }

  get formattedTotal(): string | null {
    return this.formatMoney(this.reservation?.total);
  }

  get formattedPaid(): string | null {
    return this.formatMoney(this.reservation?.paid);
  }

  get formattedOwes(): string | null {
    return this.formatMoney(this.reservation?.owes);
  }

  get paidPercent(): number {
    const total = Number(this.reservation?.total ?? 0);
    const paid = Number(this.reservation?.paid ?? 0);
    if (!total || total <= 0) {return 0;}
    return Math.max(0, Math.min(100, Math.round((paid / total) * 100)));
  }

  get hasFinancials(): boolean {
    return (
      (this.reservation?.total !== null && this.reservation?.total !== undefined)
      || (this.reservation?.paid !== null && this.reservation?.paid !== undefined)
      || (this.reservation?.owes !== null && this.reservation?.owes !== undefined)
    );
  }

  get hasDues(): boolean {
    return Boolean(this.reservation?.owes && this.reservation.owes > 0);
  }

  get hasGuidebook(): boolean {
    return Boolean(this.reservation?.guidebookUrl) || Boolean(this.reservation?.id);
  }

  onGuidebook(event: Event): void {
    event.stopPropagation();
    this.guidebookClick.emit(this.reservation);
  }

  onDetails(event: Event): void {
    event.stopPropagation();
    this.detailsClick.emit(this.reservation);
  }

  private formatMoney(value?: number | null): string | null {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {return null;}
    const currency = this.reservation?.currency || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch {
      return `$${Number(value).toFixed(2)}`;
    }
  }
}
